import { Building2, Receipt, UserCog, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type {
  AdminSearchItem,
  AdminSearchResponse,
  AdminSearchType,
} from "@/types/admin";

/**
 * The order groups are rendered (and arrow-key traversed) in the palette. Fixed
 * so the flattened index maps deterministically onto the visible list.
 */
export const COMMAND_GROUP_ORDER: readonly AdminSearchType[] = [
  "tenant",
  "user",
  "dealer",
  "invoice",
];

/** Maps each search group to its lucide icon + i18n label key. */
export const COMMAND_TYPE_META: Record<
  AdminSearchType,
  { icon: LucideIcon; labelKey: string }
> = {
  tenant: { icon: Building2, labelKey: "commandPalette.groups.tenants" },
  user: { icon: Users, labelKey: "commandPalette.groups.users" },
  dealer: { icon: UserCog, labelKey: "commandPalette.groups.dealers" },
  invoice: { icon: Receipt, labelKey: "commandPalette.groups.invoices" },
};

/** A rendered group: its type + the items belonging to it. */
export interface CommandGroup {
  type: AdminSearchType;
  items: AdminSearchItem[];
}

/**
 * Read the items for a group out of the grouped response. Kept in one place so
 * the group key ↔ response field mapping is not duplicated.
 */
function itemsForType(
  response: AdminSearchResponse,
  type: AdminSearchType,
): AdminSearchItem[] {
  switch (type) {
    case "tenant":
      return response.tenants;
    case "user":
      return response.users;
    case "dealer":
      return response.dealers;
    case "invoice":
      return response.invoices;
  }
}

/**
 * Build the ordered, non-empty groups from a grouped search response. Empty
 * groups are dropped so no group header renders without results (a dealer's
 * always-empty `dealers` group therefore never shows).
 */
export function buildCommandGroups(
  response: AdminSearchResponse | undefined,
): CommandGroup[] {
  if (!response) return [];
  return COMMAND_GROUP_ORDER.map((type) => ({
    type,
    items: itemsForType(response, type),
  })).filter((group) => group.items.length > 0);
}

/**
 * Flatten the grouped response into a single ordered list of items for arrow-key
 * indexing. Group headers are NOT part of this list (arrow keys skip them), so
 * the `activeIndex` maps directly onto a selectable item.
 */
export function flattenCommandResults(
  response: AdminSearchResponse | undefined,
): AdminSearchItem[] {
  return buildCommandGroups(response).flatMap((group) => group.items);
}
